import type { AIStore } from '..';
import type { StateCreator } from 'zustand';

import { actionLog } from '@core/store/util';

const TOOL_CONFIRMATION_STATES_KEY = 'toolConfirmation-states';

export interface StoredSettingsState {
  error?: string;
  mode: 'applied' | 'error' | 'initial' | 'skipped';
  previousValue?: string;
  setting: string;
  v: 1;
  value: string;
}

interface AiAgentSettingsToolSliceState {
  states: Record<string, StoredSettingsState>;
}

interface AiAgentSettingsToolSliceAction {
  writeState: (key: string, state: StoredSettingsState | null) => void;
}

export interface AiAgentSettingsToolSlice {
  agentSettingsTool: AiAgentSettingsToolSliceAction & AiAgentSettingsToolSliceState;
}

function isValidStoredState(value: unknown): value is StoredSettingsState {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return obj.v === 1 && !!obj.setting && !!obj.value;
}

function loadFromStorage(): Record<string, StoredSettingsState> {
  try {
    const stored = localStorage.getItem(TOOL_CONFIRMATION_STATES_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored) as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => isValidStoredState(v)) as [string, StoredSettingsState][],
    );
  } catch {
    return {};
  }
}

function saveToStorage(states: Record<string, StoredSettingsState>): void {
  try {
    localStorage.setItem(TOOL_CONFIRMATION_STATES_KEY, JSON.stringify(states));
  } catch {
    // Ignore storage failures
  }
}

const initialState: AiAgentSettingsToolSliceState = {
  states: loadFromStorage(),
};

export const createAiAgentSettingsToolSlice: StateCreator<
  AIStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AiAgentSettingsToolSlice
> = set => ({
  agentSettingsTool: {
    ...initialState,

    writeState: (key, state) => {
      set(
        s => {
          if (!state) {
            delete s.agentSettingsTool.states[key];
          } else {
            s.agentSettingsTool.states[key] = state;
          }
          saveToStorage(s.agentSettingsTool.states);
        },
        false,
        actionLog('ai.agentSettingsTool.writeState', { key, mode: state?.mode ?? null }),
      );
    },
  },
});
